/**
 * Human-readable event names sent to Mixpanel.
 *
 * The strings are byte-identical to the ones the previous untyped `makeViewEvent` /
 * `makeCompletedEvent` helpers built at runtime, so Mixpanel history, cohorts and saved
 * reports keep working. Spell them out here instead: `as const` is what gives the typed
 * `analytics.track()` its literal keys, and a name assembled by a function has none.
 */
export const Events = {
  VIEW: {
    CHECKOUT_APP: 'Viewed: Check-out App'
  },
  CHECKOUT: {
    VALIDATION_PAGE: 'Completed: Checkout Validation Page',
    CHECKOUT_DETAILS_PAGE: 'Completed: Checkout Details Page',
    VIEW_BOL_PAGE: 'Completed: Checkout View BOL Page',
    CHECKOUT_COMPLETE_PAGE: 'Completed: Checkout Complete Page'
  }
} as const;

/**
 * Context every Juno event carries.
 *
 * The warehouse and org properties are also registered as super properties by
 * `useJunoAnalytics`; they are repeated in each payload because the untyped implementation
 * did, and dropping them would change what lands in Mixpanel. The appointment and asset
 * visit ids are only known from the validation step onwards, hence optional.
 */
export interface CheckoutEventContextPayload {
  warehouse_id?: string;
  warehouse_name?: string;
  org_id?: string;
  org_name?: string;
  appointment_id?: string;
  asset_visit_id?: string;
}

/**
 * Map event keys to payload types.
 *
 * All five carry the same payload: Juno's events mark progress through one linear funnel and
 * none of them adds a property of its own. Per-event aliases of this type would read like a
 * discrimination that does not exist — give an event its own payload interface when, and only
 * when, it actually gains a field.
 */
export type JunoEventsMap = {
  [Events.VIEW.CHECKOUT_APP]: CheckoutEventContextPayload;
  [Events.CHECKOUT.VALIDATION_PAGE]: CheckoutEventContextPayload;
  [Events.CHECKOUT.CHECKOUT_DETAILS_PAGE]: CheckoutEventContextPayload;
  [Events.CHECKOUT.VIEW_BOL_PAGE]: CheckoutEventContextPayload;
  [Events.CHECKOUT.CHECKOUT_COMPLETE_PAGE]: CheckoutEventContextPayload;
};
